import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import {
  Button,
  Container,
  Box,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
  Popover,
  Skeleton,
  Typography,
  Chip,
} from '@mui/material';
import InfiniteScroll from 'react-infinite-scroller';

import { useGetGameCategoriesMutation } from 'src/api/categories/getGameCategories';
import { useGetGamesInfiniteQuery } from 'src/api/games/getGames';
import { useDeleteGameMutation } from 'src/api/games/deleteGame';
import DeleteDialog from 'src/components/pickem-admin/DeleteDialog';
import Iconify from 'src/components/iconify';
import GameDialog from 'src/components/games/game.modal';
import { ActionButton } from 'src/components/common/ActionButton';
import { useTableColumns } from 'src/hooks/useTableColumns';
import { useTimezoneStore } from 'src/store/timezoneStore';
import { useGlobalFiltersStore } from 'src/store/globalFilters.store';
import { dateUtils } from 'src/utils/date';

export default function Games() {
  const { timezone } = useTimezoneStore();
  const { languageCode } = useGlobalFiltersStore();

  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState<number>(-1);

  const [addItem, setAddItem] = useState(false);
  const [editItemId, setEditItemId] = useState<number>(-1);
  const [deleteItemId, setDeleteItemId] = useState<number>(-1);

  const [anchorEl, setAnchorEl] = useState(null);

  const openAnchor = Boolean(anchorEl);
  const id = openAnchor ? 'simple-popover' : undefined;

  // API
  const {
    data: games = [],
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetGamesInfiniteQuery({
    languageCode,
  });
  const { mutate: deleteGame, isPending: isDeletePending } =
    useDeleteGameMutation();
  const {
    mutate: getGameCategories,
    data: gameCategoriesData,
    isPending: isGetGameCategoriesPending,
  } = useGetGameCategoriesMutation();

  const gameCategories = gameCategoriesData?.categories || [];

  const handleMenu = (event: any) => {
    if (event?.currentTarget?.id) {
      setOpen(event.currentTarget);
      setOpenId(Number(event.currentTarget.id));
    } else {
      setOpen(null);
      setOpenId(-1);
    }
  };

  const deleteGameHandler = (id: number) => {
    setOpen(null);
    setOpenId(id);

    deleteGame(id);
  };

  const closeDeleteModal = () => {
    setOpen(null);
    setOpenId(-1);
    setDeleteItemId(-1);
  };

  const closeModal = () => {
    setOpen(null);
    setOpenId(-1);
    setAddItem(false);
    setEditItemId(-1);
  };

  const onInfoClick = (event: any, categoryIds: number[]) => {
    setAnchorEl(event.currentTarget);

    getGameCategories(JSON.stringify(categoryIds));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { gamesColumns } = useTableColumns();

  return (
    <>
      <Helmet>
        <title> Games </title>
      </Helmet>
      <Container>
        <Box>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => setAddItem(true)}
          >
            New Game
          </Button>
        </Box>

        <Box sx={{ width: '100%', mt: 3 }}>
          <InfiniteScroll
            pageStart={0}
            loadMore={() => fetchNextPage()}
            hasMore={hasNextPage}
            loader={
              <div className='loader' key='loader'>
                Loading ...
              </div>
            }
          >
            {isLoading ? (
              <Skeleton width='100%' height={120} />
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      {gamesColumns.map((header) => {
                        return (
                          <TableCell key={header.id}>{header.label}</TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {games.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={gamesColumns.length} align='center'>
                          No games found
                        </TableCell>
                      </TableRow>
                    ) : (
                      games.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          {gamesColumns.map((header) => {
                            switch (header.id) {
                              case 'startTime':
                              case 'endTime':
                              case 'createdAt':
                                return (
                                  <TableCell key={header.id}>
                                    {dateUtils.formatForDisplay(
                                      row[header.id],
                                      timezone
                                    )}
                                  </TableCell>
                                );
                              case 'status':
                                return (
                                  <TableCell key={header.id}>
                                    <Chip
                                      label={row.status}
                                      color={
                                        row.status === 'complete'
                                          ? 'success'
                                          : row.status === 'active'
                                          ? 'primary'
                                          : 'default'
                                      }
                                    />
                                  </TableCell>
                                );
                              case 'categoryIds':
                                return (
                                  <TableCell
                                    key={header.id}
                                    component='th'
                                    scope='row'
                                    onClick={(event) =>
                                      onInfoClick(event, row.categoryIds)
                                    }
                                  >
                                    <Iconify
                                      icon={'eva:info-fill'}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </TableCell>
                                );

                              case 'actions':
                                return (
                                  <TableCell align='right' key={header.id}>
                                    <ActionButton
                                      id={String(row.id)}
                                      onClick={handleMenu}
                                      open={open}
                                      setEditItemId={setEditItemId}
                                      setDeleteItemId={setDeleteItemId}
                                      openId={openId}
                                    />
                                  </TableCell>
                                );
                              default:
                                return (
                                  <TableCell key={header.id}>
                                    {row[header.id as keyof typeof row]}
                                  </TableCell>
                                );
                            }
                          })}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </InfiniteScroll>
        </Box>
      </Container>

      <Popover
        id={id}
        open={openAnchor}
        anchorEl={anchorEl}
        onClose={handleClose}
        style={{ padding: '10px' }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {isGetGameCategoriesPending ? (
          <Skeleton width={200} height={120} />
        ) : !gameCategories.length ? (
          <Typography p={2}>No categories found</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Difficulty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gameCategories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.id}</TableCell>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>{category.difficulty}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Popover>

      {(addItem || editItemId !== -1) && (
        <GameDialog
          open={addItem || editItemId !== -1}
          game={games?.find((game) => game.id === openId)}
          onClose={closeModal}
        />
      )}

      {deleteItemId !== -1 && (
        <DeleteDialog
          openConfirm={deleteItemId !== -1}
          type='Game'
          index={deleteItemId}
          setOpenConfirm={closeDeleteModal}
          deleteGameHandler={deleteGameHandler}
          isLoading={isDeletePending}
        />
      )}
    </>
  );
}
